import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { revealPlayer } from '../ExternalLink/MediaViewportContext';

// A modal in the browser's top layer keeps the same inline video/decoder. Unlike
// the system Fullscreen API it does not show an origin banner or rotate playback.
export default function useVideoFullscreen(root, dialog, enabled) {
  const [fullscreen, setFullscreen] = useState(false);
  const [error, setError] = useState(false);
  const session = useRef(null);
  const restoreFrame = useRef(null);

  const leave = useCallback((update = true) => {
    const saved = session.current;
    if (!saved) return;
    session.current = null;
    const node = dialog.current;
    // close() leaves the top layer; keep the dialog open as normal inline content.
    node?.close();
    node?.setAttribute('open', '');
    node?.removeAttribute('data-expanded');
    if (root.current) root.current.style.height = saved.height;
    document.body.style.overflow = saved.overflow;
    const restore = () => {
      saved.scroll.forEach(({ element, top, left }) => {
        if (element.isConnected) { element.scrollTop = top; element.scrollLeft = left; }
      });
      if (window.innerWidth !== saved.width || window.innerHeight !== saved.viewportHeight) revealPlayer(root.current);
    };
    saved.focus?.isConnected && saved.focus.focus({ preventScroll: true });
    restore();
    // Layout/ResizeObserver can settle on the next frame after leaving a modal.
    restoreFrame.current = requestAnimationFrame(restore);
    if (update) setFullscreen(false);
  }, [root, dialog]);

  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const node = dialog.current;
    const cancelled = event => { event.preventDefault(); leave(); };
    const navigated = () => leave();
    node?.addEventListener('cancel', cancelled);
    window.addEventListener('popstate', navigated);
    return () => {
      node?.removeEventListener('cancel', cancelled);
      window.removeEventListener('popstate', navigated);
      leave(false);
      cancelAnimationFrame(restoreFrame.current);
    };
  }, [dialog, enabled, leave]);

  const toggleFullscreen = useCallback(() => {
    if (session.current) { leave(); return; }
    setError(false);
    const container = root.current, node = dialog.current;
    if (!container || !node?.showModal) { setError(true); return; }
    cancelAnimationFrame(restoreFrame.current);
    const scroll = [];
    for (let element = container.parentElement; element; element = element.parentElement) {
      scroll.push({ element, top: element.scrollTop, left: element.scrollLeft });
    }
    session.current = { scroll, height: container.style.height, width: window.innerWidth, viewportHeight: window.innerHeight,
      overflow: document.body.style.overflow, focus: container.querySelector('.recorded-media__fullscreen') };
    container.style.height = `${container.getBoundingClientRect().height}px`;
    document.body.style.overflow = 'hidden';
    try {
      // showModal requires a closed dialog, even when it was displayed inline.
      node.close();
      node.setAttribute('data-expanded', 'true');
      node.showModal();
      node.focus({ preventScroll: true });
      setFullscreen(true);
    } catch {
      leave();
      setError(true);
    }
  }, [root, dialog, leave]);
  return { fullscreen, toggleFullscreen, fullscreenError: error };
}
